import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';
import moment from 'moment';

import { formatFigure, formatToFixedSeparateThousands } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';
import { apiClient } from 'Helpers/network';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function RevenueContractsIndexTableRow({
    revenue,
    onViewTerms,
    onEditTerms,
    onDeleteContract,
    onDeletingAlreadyCancelledContract,
    selecting,
    selectedIds,
    isSupplierPortalEnabled,
    onSelect,
    onAddTerms,
    fetchTicketingRevenueCommitment,
    cancelRevenueCommitmentUrl,
    updateSignedContractUrl,
    forCancel,
}) {
    const [showedCommitments, setShowedCommitments] = useState(false);
    const [canceledCommitment, setCanceledCommitment] = useState({});
    const [revenueCommitmentsCancelModalElement, setRevenueCommitmentsCancelModalElement] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const revenueCommitmentsCancelModalRef = useRef();
    const toast = useToast();

    const contractKey = useMemo(() => {
        return `${revenue.contract_type}##${revenue.contract.id}`;
    }, [revenue]);

    useEffect(() => {
        if (showedCommitments) {
            revenueCommitmentsCancelModalRef &&
                setRevenueCommitmentsCancelModalElement(new Modal(revenueCommitmentsCancelModalRef.current, modalOptions(false)));
        }
    }, [showedCommitments]);

    useEffect(() => {
        if (Object.keys(canceledCommitment).length > 0) {
            revenueCommitmentsCancelModalElement && revenueCommitmentsCancelModalElement.show();
        }
    }, [canceledCommitment]);

    const cancelCommitment = (po) => {
        setCanceledCommitment({ ...po });
    };

    const successHandler = () => {
        setCanceledCommitment({});
        fetchTicketingRevenueCommitment();
        revenueCommitmentsCancelModalElement && revenueCommitmentsCancelModalElement.hide();
    };

    const closeModal = () => {
        setCanceledCommitment({});
        revenueCommitmentsCancelModalElement && revenueCommitmentsCancelModalElement.hide();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerCancelCommitment = () => {
        setActionInProgress(true);
        axios
            .put(cancelRevenueCommitmentUrl.replace('COMMITMENT_ID', canceledCommitment.id))
            .then((response) => {
                setActionCompleted(true);
                setActionFailed(false);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    const showCommitments = (revenue) => {
        if (revenue.committedRevenues || revenue.committedTicketings) {
            setShowedCommitments(!showedCommitments);
        }
    };

    const getTotalPaymentCount = (revenue) => {
        if (forCancel) {
            return revenue.contract.term_including_trashed?.including_trashed_payment_counts
                ? revenue.contract.term_including_trashed?.including_trashed_payment_counts.total
                : revenue.contract.term_including_trashed?.payments_count;
        }

        return revenue.contract.term?.payments_count;
    };

    const getTotalMilestoneCount = (revenue) => {
        if (forCancel) {
            return revenue.contract.term_including_trashed?.including_trashed_milestone_counts
                ? revenue.contract.term_including_trashed?.including_trashed_milestone_counts.total
                : revenue.contract.term_including_trashed?.milestones_count;
        }

        return revenue.contract.term?.milestones_count;
    };

    const updateSignedContract = (signed) => {
        apiClient
            .put(updateSignedContractUrl.replace('CONTRACT_ID', revenue.contract.id), { signed })
            .then(({ data }) => {
                toast.success(data.message);
                fetchTicketingRevenueCommitment();
            })
            .catch(() => toast.error($t('Contract signed status could not be updated')));
    };

    const handleSignedColumnDoubleClick = (e, revenue) => {
        e.preventDefault();
        e.stopPropagation();
        updateSignedContract(!revenue.contract.contract_signed);
    };

    return (
        <>
            <tr key={_.snakeCase(`${revenue.depcc}_${revenue.contract.id}`)} className="tw-cursor-pointer">
                {/* Hidden checkbox cell */}
                <td className={classNames({ 'tw-hidden': !selecting })}>
                    <input type="checkbox" className="input-checkbox" checked={selectedIds.includes(contractKey)} onChange={() => onSelect(contractKey)} />
                </td>
                <td className="tw-cursor-pointer" onClick={() => showCommitments(revenue)}>
                    <Tooltip
                        tooltipContent={
                            forCancel
                                ? revenue.contract?.term_including_trashed?.supply_scope
                                    ? revenue.contract?.term_including_trashed?.supply_scope
                                    : '-'
                                : revenue.contract?.term?.supply_scope
                                  ? revenue.contract?.term?.supply_scope
                                  : '-'
                        }
                        tooltipType="text"
                        offset={[16, 16]}
                        allowTooltip={
                            forCancel
                                ? revenue.contract?.term_exists && revenue.contract?.term_including_trashed?.supply_scope
                                : revenue.contract?.term_exists && revenue.contract?.term?.supply_scope
                        }
                    >
                        <div className={`tw-cursor-pointer ${(revenue.committedRevenues || revenue.committedTicketings) && 'tw-text-blue'}`}>
                            {revenue.contract?.contract_reference}
                        </div>
                    </Tooltip>
                </td>
                <td onDoubleClick={(e) => handleSignedColumnDoubleClick(e, revenue)} className="tw-cursor-pointer tw-select-none tw-h-full">
                    <Tooltip
                        tooltipContent={revenue.contract.contract_signed ? $t('Double click to mark as unsigned') : $t('Double click to mark as signed')}
                        tooltipType="text"
                    >
                        <div className="tw-h-4 tw-mx-auto tw-flex tw-items-center tw-justify-center">
                            {revenue.contract.contract_signed && <CircleCheckIcon className="tw-w-4 tw-h-4 tw-text-green-500" />}
                        </div>
                    </Tooltip>
                </td>
                <td>{revenue.contract?.contractable?.name}</td>
                <td>
                    <div className="tw-flex tw-overflow-x-auto tw-gap-1.5">
                        <div className="tw-bg-charcoal-15 tw-text-xs tw-text-nowrap tw-font-medium tw-px-1.5 tw-py-1 tw-rounded-lg">{revenue.depcc}</div>
                    </div>
                </td>
                <td className="inbud-numeric-cell">
                    {forCancel ? '' : formatFigure(revenue.contract?.cashflow.total, (value) => formatToFixedSeparateThousands(value, ','))}
                </td>
                <td className="inbud-numeric-cell">
                    {forCancel ? '' : formatFigure(revenue.contract?.actual_spend, (value) => formatToFixedSeparateThousands(value, ','))}
                </td>
                <td className={classNames('inbud-numeric-cell', { 'tw-text-red-75': revenue.contract?.variance < 0 })}>
                    {forCancel ? '' : formatFigure(revenue.contract?.variance)}
                </td>
                <td>
                    {forCancel ? revenue.contract?.term_including_trashed?.owner?.first_name : revenue.contract.term?.owner?.first_name}{' '}
                    {forCancel ? revenue.contract?.term_including_trashed?.owner?.last_name : revenue.contract.term?.owner?.last_name}
                </td>
                <td className="inbud-numeric-cell">{formatFigure(getTotalPaymentCount(revenue), (x) => x)}</td>
                <td className="inbud-numeric-cell">{formatFigure(getTotalMilestoneCount(revenue), (x) => x)}</td>
                <td>
                    <div className="tw-flex tw-items-center tw-gap-x-3 ">
                        {forCancel ? (
                            <>
                                <TextButton
                                    variant="danger"
                                    onClick={() => onDeletingAlreadyCancelledContract(revenue.contract, revenue.contract_type, revenue.committed_id)}
                                >
                                    <TrashIcon className="tw-w-3 tw-h-3" />
                                </TextButton>
                            </>
                        ) : (
                            <>
                                {revenue.term_exists ? (
                                    <>
                                        <TextButton variant="primary" onClick={() => onViewTerms(revenue.contract)}>
                                            <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View Terms')}
                                        </TextButton>
                                        <TextButton variant="primary" onClick={() => onEditTerms(revenue.contract)}>
                                            <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit Terms')}
                                        </TextButton>
                                    </>
                                ) : (
                                    <TextButton variant="primary" onClick={() => onAddTerms(revenue.contract)}>
                                        <CirclePlusIcon className="tw-w-3 tw-h-3" /> {$t('Add Terms')}
                                    </TextButton>
                                )}
                                {isSupplierPortalEnabled && !!revenue.contract.settings_portal_url && (
                                    <TextButton variant="primary" onClick={() => (window.location.href = revenue.contract.settings_portal_url)}>
                                        <EyeIcon className="tw-w-3 tw-h-3" /> {$t('Portal Settings')}
                                    </TextButton>
                                )}
                                <TextButton variant="danger" onClick={() => onDeleteContract(revenue.contract, revenue.contract_type, revenue.committed_id)}>
                                    <TrashIcon className="tw-w-3 tw-h-3" />
                                </TextButton>
                            </>
                        )}
                    </div>
                </td>
            </tr>
            {showedCommitments && (
                <tr>
                    <td colSpan="12">
                        <h5 className="table__header tw-mb-3">{$t('Commitments')}:</h5>
                        <div className="inbud-table_wrapper">
                            <table className="inbud-table">
                                <thead>
                                    <tr>
                                        <th>{$t('Value')}</th>
                                        <th>{$t('Status')}</th>
                                        <th>{$t('Approval Date')}</th>
                                        <th>{$t('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {revenue?.committedRevenues?.map((committedRevenue) => {
                                        return (
                                            <tr key={committedRevenue.id}>
                                                <td>{committedRevenue.cashflow.total}</td>
                                                <td>{forCancel ? $t('Cancelled') : $t('Approved')}</td>
                                                <td>{moment(committedRevenue.updated_at).format('YYYY-MM-DD')}</td>
                                                <td>
                                                    {!forCancel && (
                                                        <div className="tw-flex tw-items-center tw-gap-x-3">
                                                            <TextButton variant="danger" onClick={() => cancelCommitment(committedRevenue)}>
                                                                <CircleXMarkIcon className="tw-w-3 tw-h-3" /> {$t('Cancel')}
                                                            </TextButton>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {revenue?.committedTicketings?.map((committedTicketing) => {
                                        return (
                                            <tr key={committedTicketing.id}>
                                                <td>{committedTicketing.amount}</td>
                                                <td>{forCancel ? $t('Cancelled') : $t('Approved')}</td>
                                                <td>{moment(committedTicketing.updated_at).format('YYYY-MM-DD')}</td>
                                                <td></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            <ConfirmationModal
                                closeHandler={closeModal}
                                successHandler={successHandler}
                                triggerAction={triggerCancelCommitment}
                                cleanStates={cleanConfirmationModalStates}
                                allowAction={Object.keys(canceledCommitment).length > 0}
                                modalId={'cancel-commitment'}
                                modalRef={revenueCommitmentsCancelModalRef}
                                modalElement={revenueCommitmentsCancelModalElement}
                                request={actionInProgress}
                                actionCompleted={actionCompleted}
                                actionFailed={actionFailed}
                                warningMsg={$t('Are you sure you want to cancel this commitment')}
                                confirmationMsg={$t('Cancel the Revenue Commitment')}
                                actionCompletedMsg={$t('Revenue Commitment canceled')}
                                actionFailedMsg={$t('Revenue Commitment could not be canceled')}
                                title={$t('Cancellation of Revenue Commitment')}
                                cancelButtonFirst={true}
                                submitButtonVariant="primary"
                                cancelButtonVariant="danger"
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Confirm')}
                            />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
